import { useAuth, useCachedFetch } from '@structural-climbing/auth'
import type { PayablePlan } from './types'

const EMPTY: PayablePlan[] = []

/** Prefix-invalidate with `invalidateCachedFetch('payable-plans:')`. */
export function payablePlansKey(gymId: string | null | undefined): string | null {
  return gymId ? `payable-plans:${gymId}` : null
}

/**
 * The plans this gym sells online, for the member looking at them.
 *
 * One call answers the whole question of whether to offer paying at all. The
 * Worker returns an empty list unless the gym switched payments on, Stripe
 * agrees it can charge, and at least one active main plan is marked payable, so
 * the member app never has to assemble that judgement from parts it would then
 * have to keep in step.
 *
 * `enabled` is what keeps this off the wire on screens that could not show a
 * pay button anyway: the key goes null, and `useCachedFetch` idles.
 */
export function useMyPayablePlans(
  gymId: string | null | undefined,
  enabled = true,
): { plans: PayablePlan[]; loading: boolean; refresh: () => Promise<void> } {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data, loading, refresh } = useCachedFetch<PayablePlan[]>(
    enabled ? payablePlansKey(gymId) : null,
    async () => {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-payable-plans?gym_id=${encodeURIComponent(gymId!)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      // Every refusal means the same thing here: nothing to sell. A child
      // account gets a 403, which is correct to read as "no plans" rather than
      // as an error worth showing anybody.
      if (!res.ok) return EMPTY
      const body = (await res.json()) as { plans?: PayablePlan[] }
      return body.plans ?? EMPTY
    },
  )

  return { plans: data ?? EMPTY, loading, refresh }
}
